import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import usePerformance from "@/components/performance/usePerformance";
import { RangeSelector, CompareSelector } from "@/components/performance/Selectors";
import TimelineCard from "@/components/performance/TimelineCard";
import KpiGrid from "@/components/performance/KpiGrid";
import ScoreBreakdown from "@/components/performance/ScoreBreakdown";
import FunnelCard from "@/components/performance/FunnelCard";
import StalledCasesCard from "@/components/performance/StalledCasesCard";
import ExportMenu from "@/components/performance/ExportMenu";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const fmtRange = (p) => {
  if (!p?.from || !p?.to) return p?.label || null;
  const f = (iso) => new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
  return `${f(p.from)} – ${f(p.to)}`;
};

const TABS = [
  { id: "overview", label: tfr("Vue d'ensemble") },
  { id: "activity", label: tfr("Activité") },
  { id: "pipeline", label: "Pipeline" },
];

export default function Performance() {
  const { state, setState, data, loading, error, reload } = usePerformance();
  const [scoreOpen, setScoreOpen] = useState(false);

  const compLabel = fmtRange(data?.comparisonPeriod);
  const tab = state.tab;
  const showTimeline = tab === "overview" || tab === "activity";
  const showKpis = tab === "overview" || tab === "activity";
  const showPipeline = tab === "overview" || tab === "pipeline";

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 sm:px-6 pt-6 pb-12 space-y-4" data-testid="performance-page">
      {/* HEADER */}
      <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">Performance</h1>
          <p className="text-sm text-muted-foreground mt-1">{tfr("Suivez la progression de vos indicateurs clés dans le temps.")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="perf-controls">
          <RangeSelector state={state} periodLabel={fmtRange(data?.period)} onChange={setState} />
          <CompareSelector state={state} onChange={setState} />
          <ExportMenu data={data} />
        </div>
      </header>

      {/* TABS */}
      <div role="tablist" aria-label="Sections performance"
        className="flex gap-1 overflow-x-auto border-b border-border -mx-4 px-4 sm:mx-0 sm:px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id}
            onClick={() => setState({ tab: t.id })}
            data-testid={`perf-tab-${t.id}`}
            className={cn("px-3 h-11 text-sm whitespace-nowrap shrink-0 border-b-2 -mb-px transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab === t.id ? "border-athlynx text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {t.label}
          </button>
        ))}
        <Link to="/performance/contracts" data-testid="perf-tab-contracts"
          className="px-3 h-11 text-sm whitespace-nowrap shrink-0 border-b-2 -mb-px border-transparent text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {tfr("Contrats")}
          <span className="text-[10px] uppercase tracking-wider rounded-full border border-athlynx/30 bg-athlynx/[0.07] text-athlynx px-1.5 py-0.5">{tfr("Nouveau")}</span>
        </Link>
      </div>

      {error ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" role="alert" data-testid="perf-error">
          {tfr("Impossible de charger vos données de performance.")}
          <Button variant="outline" size="sm" className="ml-3" onClick={reload}>{tfr("Réessayer")}</Button>
        </div>
      ) : data?.insufficientHistory ? (
        <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-10 text-center" data-testid="perf-empty">
          <p className="text-base font-semibold">Pas encore assez d'historique</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {tfr("Continuez à enregistrer vos interactions, changements de statut et résultats pour suivre votre progression dans le temps.")}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setState({ range: "all", from: null, to: null })} data-testid="perf-empty-alltime">{tfr("Voir tout l'historique")}</Button>
            <Button variant="outline" size="sm" onClick={() => setState({ range: "12m", from: null, to: null })}>{tfr("Élargir la période")}</Button>
          </div>
        </div>
      ) : (
        <>
          {showTimeline && (
            <TimelineCard
              metric={state.metric}
              onMetricChange={(m) => setState({ metric: m })}
              timeline={data?.timeline}
              comparisonLabel={compLabel ? `${tfr("Période précédente")} (${compLabel})` : null}
              loading={loading && !data}
            />
          )}
          {showKpis && (
            <KpiGrid kpis={data?.kpis} comparisonLabel={compLabel} onScoreClick={() => setScoreOpen(true)} loading={loading && !data} />
          )}
          {showPipeline && (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,0.9fr)_minmax(420px,1.4fr)] gap-4" data-testid="perf-bottom-grid">
              <FunnelCard funnel={data?.funnel} loading={loading && !data} />
              <StalledCasesCard cases={data?.stalledCases} loading={loading && !data} />
            </div>
          )}
        </>
      )}

      <ScoreBreakdown open={scoreOpen} onOpenChange={setScoreOpen} score={data?.score} />
    </div>
  );
}
